import { ReactNode, useMemo } from "react";

import { GenericExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { PostPurchaseSetupStartMessages } from "@intelligems/ig-utils/post-purchase-setup";
import {
  getMissingScopes,
  ScopeKey,
} from "@intelligems/ig-utils/experience-test-type-scopes";

import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { PostPurchaseNoAccessMessage } from "@src/components/Dialogs/CreateExperienceDialog/PostPurchaseNoAccessMessage";
import {
  useHasPostPurchaseAccess,
  usePostPurchaseSetupIssue,
} from "@src/components/LimitAccess/hooks";

import { PostPurchaseAudienceOverlapMessage } from "./PostPurchaseAudienceOverlapMessage";
import { CartTransformConflictMessage } from "./CartTransformConflictMessage";

import { useIsPriceTestIntegrationReady } from "./useIsPriceTestIntegrationReady";
import { useIsShippingTestAlreadyActive } from "./useIsShippingTestAlreadyActive";
import { useIsCartTransformTestConflictActive } from "./useIsCartTransformTestConflictActive";
import { useHasLivePostPurchaseWithSameAudience } from "./useHasLivePostPurchaseWithSameAudience";
import { useIsTestLimitHit } from "./useIsTestLimitHit";

export const useCanStartExperience = (
  experience: GenericExperienceFe
): { canStart: boolean; disabledMessage: ReactNode } => {
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const scopeMap = useBooleanWebUiScopeMap();
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const isShippingTestAlreadyActive = useIsShippingTestAlreadyActive();
  const isPricingCartTransformConflict = useIsCartTransformTestConflictActive(
    experience.id,
    "hasTestPricing",
    Boolean(experience.testTypes.hasTestPricing)
  );
  const isShippingCartTransformConflict = useIsCartTransformTestConflictActive(
    experience.id,
    "hasTestShipping",
    Boolean(experience.testTypes.hasTestShipping)
  );
  const isTestLimitHit = useIsTestLimitHit();
  const hasLivePostPurchaseWithSameAudience =
    useHasLivePostPurchaseWithSameAudience(experience);
  const hasPostPurchaseAccess = useHasPostPurchaseAccess();
  const postPurchaseSetupIssue = usePostPurchaseSetupIssue();
  const isAdmin = useIsAdmin();

  return useMemo(() => {
    const category =
      experience.category === "experiment" ? "test" : "personalization";

    if (!experience) {
      return { canStart: false, disabledMessage: "" };
    }

    if (!isSubscriptionActive) {
      return {
        canStart: false,
        disabledMessage: `Approve billing to start this ${category}`,
      };
    }

    const missingScopes = getMissingScopes({
      action: "start",
      testTypes: experience.testTypes,
      category: experience.category,
      scopes: (Object.keys(scopeMap) as ScopeKey[]).filter(
        (key) => scopeMap[key]
      ),
    });

    // Admins bypass plan-derived limits (concurrent-test limit and plan
    // scopes) but still hit the operational checks below.
    if (isTestLimitHit && !isAdmin) {
      return {
        canStart: false,
        disabledMessage: `You have reached the maximum number of active tests. End an existing test to start a new one.`,
      };
    }

    if (missingScopes.length > 0 && !isAdmin) {
      return {
        canStart: false,
        disabledMessage: `Upgrade your plan to start this ${missingScopes.map(({ label }) => label).join(", ")} ${category}`,
      };
    }

    if (experience.testTypes.hasTestPricing) {
      if (!isPriceTestIntegrationReady) {
        return {
          canStart: false,
          disabledMessage: `Complete your pricing integration to start this ${category}`,
        };
      }
      if (isPricingCartTransformConflict && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: CartTransformConflictMessage("Shipping Test"),
        };
      }
    }

    if (experience.testTypes.hasTestShipping) {
      if (isShippingTestAlreadyActive && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: `Only one shipping test can be run at a time. End your current shipping test to start a new one.`,
        };
      }
      if (isShippingCartTransformConflict && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: CartTransformConflictMessage("Price Test"),
        };
      }
    }

    if (experience.testTypes.hasTestPostPurchase) {
      if (!hasPostPurchaseAccess && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: <PostPurchaseNoAccessMessage />,
        };
      }
      // Also covers starting from a table row, where the store's setup screen
      // never shows.
      if (postPurchaseSetupIssue && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage:
            PostPurchaseSetupStartMessages[postPurchaseSetupIssue],
        };
      }
      if (
        hasLivePostPurchaseWithSameAudience &&
        experience.status !== "started"
      ) {
        return {
          canStart: false,
          disabledMessage: PostPurchaseAudienceOverlapMessage,
        };
      }
    }

    return {
      canStart: true,
      disabledMessage: "",
    };
  }, [
    experience,
    isSubscriptionActive,
    scopeMap,
    isPriceTestIntegrationReady,
    isShippingTestAlreadyActive,
    isPricingCartTransformConflict,
    isShippingCartTransformConflict,
    isTestLimitHit,
    hasLivePostPurchaseWithSameAudience,
    hasPostPurchaseAccess,
    postPurchaseSetupIssue,
    isAdmin,
  ]);
};
